import { useState } from 'react';

export interface FAQItem {
  q: string;
  a: string;
}

export interface SharedFAQSectionProps {
  title?: string;
  faqs: FAQItem[];
}

export default function SharedFAQSection({
  title = 'Roofing Questions Answered',
  faqs,
}: SharedFAQSectionProps) {
  const [open, setOpen] = useState<number | null>(0);

  const [titleStart, titleEnd] = [
    title.split(' ').slice(0, -1).join(' '),
    title.split(' ').slice(-1)[0],
  ];

  return (
    <section id="faq" className="py-28 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-block w-16 h-1 bg-brand-orange rounded-full mb-6"></div>
          <h2 className="font-heading font-900 text-charcoal text-5xl md:text-6xl uppercase tracking-tight leading-none mb-4">
            {titleStart}<br />
            <span className="text-brand-orange">{titleEnd}</span>
          </h2>
          <p className="font-body text-charcoal/60 text-base">
            Everything you need to know — plain and simple.
          </p>
        </div>

        {/* Accordion */}
        <div className="bg-white rounded-2xl border border-surface-dark overflow-hidden">
          {faqs.map((faq, i) => (
            <div key={i} className="border-b border-surface-dark last:border-b-0">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-start justify-between gap-4 px-8 py-6 text-left cursor-pointer hover:bg-surface/40 transition-colors duration-150"
              >
                <span className="font-heading font-800 text-charcoal text-lg uppercase tracking-wide leading-tight">
                  {faq.q}
                </span>
                <div className="w-8 h-8 flex items-center justify-center bg-surface rounded-lg flex-shrink-0 mt-0.5">
                  <i
                    className={`text-brand-orange text-xl transition-transform duration-200 ${
                      open === i ? 'ri-subtract-line' : 'ri-add-line'
                    }`}
                  ></i>
                </div>
              </button>
              {open === i && (
                <div className="px-8 pb-6">
                  <p className="font-body text-charcoal/70 text-base leading-relaxed border-l-2 border-brand-orange pl-4">
                    {faq.a}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
}
